import {
  LayoutDashboard,
  MapPin,
  Package,
  ClipboardList,
  CalendarClock,
  CheckSquare,
  FileText,
  ClipboardCheck,
  AlertTriangle,
  Calendar,
  FileBarChart,
  TrendingUp,
  Users,
  UsersRound,
  Settings,
  Clock,
  Boxes,
  Truck,
  Shield,
  SlidersHorizontal,
  ShieldAlert,
  Gauge,
  ArrowUpCircle,
  Plug,
  Activity,
} from "lucide-react";
import type { NavGroup } from "@/shared/types/navigation";

/**
 * Navigation items for the Veltro sidebar, organized by group.
 * Labels use i18n keys — the Sidebar translates them via t(item.labelKey).
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    label: "Overview",
    labelKey: "nav.groups.overview",
    items: [
      { label: "Dashboard",       href: "/",             icon: LayoutDashboard },
    ],
  },
  {
    label: "Assets & Facilities",
    labelKey: "nav.groups.assetsFacilities",
    items: [
      { label: "Locations",       href: "/locations",       icon: MapPin,  featureFlag: "locations" },
      { label: "Assets",          href: "/assets",          icon: Package, featureFlag: "assets" },
      { label: "Inventory",       href: "/inventory",       icon: Boxes,   featureFlag: "inventory" },
      { label: "Meter Readings",  href: "/meter-readings",  icon: Gauge,   featureFlag: "meterReadings" },
    ],
  },
  {
    label: "Work Management",
    labelKey: "nav.groups.workManagement",
    items: [
      { label: "Work Orders",     href: "/work-orders",     icon: ClipboardList, featureFlag: "workOrders" },
      { label: "PM Schedules",    href: "/pm-schedules",    icon: CalendarClock, featureFlag: "preventiveMaintenance" },
      { label: "Checklists",      href: "/checklists",      icon: CheckSquare,   featureFlag: "checklists" },
      { label: "Labor Tracking",  href: "/labor-tracking",  icon: Clock,         featureFlag: "laborTracking" },
      { label: "Vendors",         href: "/vendors",         icon: Truck,         featureFlag: "vendors" },
    ],
  },
  {
    label: "Safety & Compliance",
    labelKey: "nav.groups.safetyCompliance",
    items: [
      { label: "Incidents",       href: "/incidents",         icon: ShieldAlert,   featureFlag: "incidents" },
      { label: "Escalation Rules", href: "/escalation-rules", icon: ArrowUpCircle, featureFlag: "escalationRules" },
    ],
  },
  {
    label: "Inspections",
    labelKey: "nav.groups.inspections",
    items: [
      { label: "Templates",       href: "/templates",            icon: FileText,       featureFlag: "templates" },
      { label: "Inspections",     href: "/inspections",          icon: ClipboardCheck, featureFlag: "inspections" },
      { label: "Actions",         href: "/actions",              icon: AlertTriangle,  featureFlag: "actions" },
      { label: "Schedules",       href: "/inspection-schedules", icon: Calendar,       featureFlag: "inspectionScheduling" },
      { label: "Reports",         href: "/reports",              icon: FileBarChart,   featureFlag: "inspections" },
      { label: "Analytics",       href: "/analytics",            icon: TrendingUp,     featureFlag: "inspectionAnalytics" },
    ],
  },
  {
    label: "Analytics",
    labelKey: "nav.groups.analytics",
    items: [
      { label: "Work Orders",   href: "/analytics/work-orders",  icon: ClipboardList, featureFlag: "dashboard" },
      { label: "Asset Health",   href: "/analytics/assets",       icon: Activity,      featureFlag: "dashboard" },
      { label: "Preventive",     href: "/analytics/preventive",   icon: CalendarClock, featureFlag: "preventiveMaintenance" },
      { label: "Team",           href: "/analytics/team",         icon: Users,         featureFlag: "dashboard" },
      { label: "Safety",         href: "/analytics/safety",       icon: ShieldAlert,   featureFlag: "incidents" },
    ],
  },
  {
    label: "Administration",
    labelKey: "nav.groups.administration",
    items: [
      { label: "Users",            href: "/users",          icon: Users              },
      { label: "Teams",            href: "/teams",          icon: UsersRound         },
      { label: "Audit Log",       href: "/audit-log",      icon: Shield             },
      { label: "Custom Fields",   href: "/custom-fields",  icon: SlidersHorizontal, featureFlag: "customFields" },
      { label: "Integrations",    href: "/integrations",   icon: Plug,              featureFlag: "integrations" },
      { label: "Settings",        href: "/settings",       icon: Settings           },
    ],
  },
];

export const PATH_LABELS: Record<string, string> = {
  "/": "Dashboard",
  "locations": "Locations",
  "assets": "Assets",
  "work-orders": "Work Orders",
  "pm-schedules": "PM Schedules",
  "checklists": "Checklists",
  "labor-tracking": "Labor Tracking",
  "inventory": "Inventory",
  "vendors": "Vendors",
  "incidents": "Incidents",
  "meter-readings": "Meter Readings",
  "templates": "Templates",
  "inspections": "Inspections",
  "actions": "Actions",
  "inspection-schedules": "Schedules",
  "reports": "Reports",
  "analytics": "Analytics",
  "users": "Users",
  "teams": "Teams",
  "audit-log": "Audit Log",
  "custom-fields": "Custom Fields",
  "escalation-rules": "Escalation Rules",
  "integrations": "Integrations",
  "settings": "Settings",
  "new": "New",
  "edit": "Edit"
};

/**
 * Route path constants — avoids magic strings in navigate() calls.
 */
export const ROUTE_PATHS = {
  dashboard:       "/",
  locations:       "/locations",
  assets:          "/assets",
  assetsNew:       "/assets/new",
  assetDetail:     (id: string) => `/assets/${id}`,
  assetEdit:       (id: string) => `/assets/${id}/edit`,
  workOrders:      "/work-orders",
  workOrderNew:    "/work-orders/new",
  workOrderDetail: (id: string) => `/work-orders/${id}`,
  workOrderEdit:   (id: string) => `/work-orders/${id}/edit`,
  pmSchedules:     "/pm-schedules",
  pmScheduleNew:   "/pm-schedules/new",
  pmScheduleDetail:(id: string) => `/pm-schedules/${id}`,
  checklists:      "/checklists",
  checklistNew:    "/checklists/new",
  checklistDetail: (id: string) => `/checklists/${id}`,
  templates:             "/templates",
  inspections:           "/inspections",
  actions:               "/actions",
  inspectionSchedules:   "/inspection-schedules",
  reports:               "/reports",
  analytics:             "/analytics",
  laborTracking:   "/labor-tracking",
  inventory:       "/inventory",
  vendors:         "/vendors",
  incidents:       "/incidents",
  meterReadings:   "/meter-readings",
  teams:           "/teams",
  users:           "/users",
  auditLog:        "/audit-log",
  customFields:    "/custom-fields",
  escalationRules: "/escalation-rules",
  integrations:    "/integrations",
  settings:        "/settings",
} as const;

/**
 * Work Order priority → SLA hours (configurable per tenant in Phase B).
 */
export const PRIORITY_SLA_HOURS: Record<string, number> = {
  critical: 2,
  high:     8,
  medium:   24,
  low:      72,
};

/**
 * Priority label → Tailwind color class.
 */
export const PRIORITY_COLORS: Record<string, string> = {
  critical: "text-red-600 bg-red-50",
  high:     "text-orange-600 bg-orange-50",
  medium:   "text-yellow-600 bg-yellow-50",
  low:      "text-blue-600 bg-blue-50",
};

/**
 * Work Order status → Tailwind color class.
 */
export const STATUS_COLORS: Record<string, string> = {
  open:        "text-blue-700 bg-blue-50",
  in_progress: "text-yellow-700 bg-yellow-50",
  on_hold:     "text-purple-700 bg-purple-50",
  completed:   "text-green-700 bg-green-50",
  closed:      "text-gray-600 bg-gray-100",
};

